"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  type BaseTenantId,
  getTenant,
  getTenantVariant,
  type ParentTenantId,
  type VariantTenantId,
} from "@tilli-internal/tenant-data";

export interface TenantContextValue {
  id: BaseTenantId;
  variantId: VariantTenantId | null;
}
const TenantContext = createContext<TenantContextValue | null>(null);

type TenantProviderStaticProps = {
  strategy?: "static";
  tenantId: BaseTenantId;
  variantId: VariantTenantId | null;
};
type TenantProviderSelectorProps = {
  strategy?: "selector";
  selector?: string;
  serverTenantId?: BaseTenantId;
  serverVariantId?: VariantTenantId | null;
};

export type TenantProviderProps =
  | TenantProviderSelectorProps
  | TenantProviderStaticProps;

const TenantProviderStatic: React.FC<
  React.PropsWithChildren<Omit<TenantProviderStaticProps, "strategy">>
> = ({ tenantId, variantId, children }) => {
  const tenant = useRef(
    variantId
      ? getTenantVariant(tenantId as ParentTenantId, variantId as never)
      : getTenant(tenantId),
  ).current;

  return (
    <TenantContext.Provider value={{ ...tenant, variantId }}>
      {children}
    </TenantContext.Provider>
  );
};

const extractTenantFromElement = (element: HTMLElement) => {
  const tenantId = element.dataset.tenant as BaseTenantId | null;
  const variantId = element.dataset["tenant-variant"] as VariantTenantId | null;

  if (!tenantId) {
    throw new Error(
      "TenantProviderSelector: data-tenant attribute is required",
    );
  }

  return { id: tenantId, variantId };
};

const TenantProviderSelector: React.FC<
  React.PropsWithChildren<Omit<TenantProviderSelectorProps, "strategy">>
> = ({ selector: _selector, serverTenantId, serverVariantId, children }) => {
  const selector = _selector || "[data-tenant]";
  const [tenant, setTenant] = useState<TenantContextValue>(() => {
    const element =
      typeof document !== "undefined"
        ? document.querySelector<HTMLElement>(selector)
        : null;
    if (!element) {
      return {
        id: serverTenantId ?? "tilli",
        variantId: serverVariantId ?? null,
      } as TenantContextValue;
    }
    return extractTenantFromElement(element);
  });

  useEffect(() => {
    const element = document.querySelector<HTMLElement>(selector);

    if (element) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "attributes" && mutation.target === element) {
            setTenant(extractTenantFromElement(element));
          }
        });
      });

      observer.observe(element, {
        attributeFilter: ["data-tenant", "data-tenant-variant"],
        subtree: false,
        characterData: false,
        childList: false,
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [selector]);

  return (
    <TenantContext.Provider
      value={{ id: tenant?.id, variantId: tenant?.variantId ?? null }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const TenantProvider: React.FC<
  React.PropsWithChildren<TenantProviderProps>
> = ({ strategy = "static", children, ...props }) => {
  if (strategy === "static") {
    return (
      <TenantProviderStatic {...(props as TenantProviderStaticProps)}>
        {children}
      </TenantProviderStatic>
    );
  }

  return (
    <TenantProviderSelector {...(props as TenantProviderSelectorProps)}>
      {children}
    </TenantProviderSelector>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);

  if (context === null) {
    // TODO: determine some heuristic that lets us know that we SHOULD expect a tenant but we aren't getting one and log something nefarious and evil
    console.warn(
      "useTenant: No tenant context found, defaulting to 'tilli'. This may indicate a misconfiguration in the app.",
    );
    return {
      id: "tilli" as BaseTenantId,
      variantId: null,
    } as TenantContextValue;
  }

  return context;
};
